<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();

const config = useMapGetter('disparoConfig/getConfig');
const permittedUserIds = useMapGetter('disparoConfig/getPermittedUserIds');
const uiFlags = useMapGetter('disparoConfig/getUIFlags');
const agents = useMapGetter('agents/getAgents');

const webhookUrl = ref('');
const webhookSecret = ref('');
const selectedUserIds = ref(new Set());

const secretIsSet = computed(() => Boolean(config.value?.webhook_secret_set));

const isSavingWebhook = computed(() => uiFlags.value.isUpdating);
const isSavingPerms = computed(() => uiFlags.value.isUpdatingPermissions);

onMounted(async () => {
  await Promise.all([
    store.dispatch('disparoConfig/get'),
    store.dispatch('disparoConfig/fetchPermissions'),
    store.dispatch('agents/get'),
  ]);
});

watch(
  config,
  newConfig => {
    if (newConfig) webhookUrl.value = newConfig.webhook_url || '';
  },
  { immediate: true }
);

watch(
  permittedUserIds,
  ids => {
    selectedUserIds.value = new Set(ids || []);
  },
  { immediate: true }
);

const isUserSelected = userId => selectedUserIds.value.has(userId);

const toggleUser = userId => {
  if (selectedUserIds.value.has(userId)) selectedUserIds.value.delete(userId);
  else selectedUserIds.value.add(userId);
  selectedUserIds.value = new Set(selectedUserIds.value);
};

const saveWebhook = async () => {
  if (!webhookUrl.value) {
    useAlert(t('DISPARO_SETTINGS.WEBHOOK.VALIDATION.REQUIRED'));
    return;
  }
  const payload = { webhook_url: webhookUrl.value };
  if (webhookSecret.value) payload.webhook_secret = webhookSecret.value;
  await store.dispatch('disparoConfig/save', payload);
  webhookSecret.value = '';
  useAlert(t('DISPARO_SETTINGS.WEBHOOK.SAVED'));
};

const savePermissions = async () => {
  await store.dispatch(
    'disparoConfig/updatePermissions',
    Array.from(selectedUserIds.value)
  );
  useAlert(t('DISPARO_SETTINGS.PERMISSIONS.SAVED'));
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t('DISPARO_SETTINGS.TITLE')"
        :description="t('DISPARO_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-10 mt-4">
        <section class="max-w-2xl flex flex-col gap-3">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('DISPARO_SETTINGS.WEBHOOK.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('DISPARO_SETTINGS.WEBHOOK.DESCRIPTION') }}
          </p>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('DISPARO_SETTINGS.WEBHOOK.LABEL') }}
            </label>
            <Input
              v-model="webhookUrl"
              :placeholder="t('DISPARO_SETTINGS.WEBHOOK.PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('DISPARO_SETTINGS.WEBHOOK.SECRET_LABEL') }}
            </label>
            <Input
              v-model="webhookSecret"
              type="password"
              :placeholder="
                secretIsSet
                  ? t('DISPARO_SETTINGS.WEBHOOK.SECRET_PLACEHOLDER_SET')
                  : t('DISPARO_SETTINGS.WEBHOOK.SECRET_PLACEHOLDER')
              "
            />
            <p class="text-xs text-n-slate-11">
              {{ t('DISPARO_SETTINGS.WEBHOOK.SECRET_HELP') }}
            </p>
          </div>
          <div>
            <Button
              :label="t('DISPARO_SETTINGS.WEBHOOK.SAVE')"
              :is-loading="isSavingWebhook"
              :disabled="isSavingWebhook"
              @click="saveWebhook"
            />
          </div>
        </section>

        <section class="max-w-2xl flex flex-col gap-3">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('DISPARO_SETTINGS.PERMISSIONS.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('DISPARO_SETTINGS.PERMISSIONS.DESCRIPTION') }}
          </p>
          <ul
            class="flex flex-col divide-y divide-n-slate-4 border border-n-slate-4 rounded-md"
          >
            <li
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center justify-between px-4 py-3"
            >
              <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-n-slate-12">{{
                  agent.name
                }}</span>
                <span class="text-xs text-n-slate-11">{{ agent.email }}</span>
                <span
                  v-if="agent.role === 'administrator'"
                  class="text-xs px-2 py-0.5 rounded bg-n-alpha-3 text-n-slate-11"
                >
                  {{ t('DISPARO_SETTINGS.PERMISSIONS.ADMIN_BADGE') }}
                </span>
              </div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="isUserSelected(agent.id)"
                  :disabled="agent.role === 'administrator'"
                  @change="toggleUser(agent.id)"
                />
                <span class="text-sm text-n-slate-11">
                  {{ t('DISPARO_SETTINGS.PERMISSIONS.ALLOW') }}
                </span>
              </label>
            </li>
          </ul>
          <div>
            <Button
              :label="t('DISPARO_SETTINGS.PERMISSIONS.SAVE')"
              :is-loading="isSavingPerms"
              :disabled="isSavingPerms"
              @click="savePermissions"
            />
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
